import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { HiCog, HiPlus, HiDotsVertical, HiTrash, HiPencil, HiViewBoards, HiViewList, HiTable, HiCalendar, HiDocumentText, HiUserGroup } from 'react-icons/hi'
import { projectsApi, boardsApi } from '../api/client'
import { useProjectContext } from '../context/ProjectContext'
import BoardView from '../components/boards/BoardView'
import ListView from '../components/boards/ListView'
import TableView from '../components/boards/TableView'
import CalendarView from '../components/boards/CalendarView'
import CreateBoardModal from '../components/common/CreateBoardModal'
import EditProjectModal from '../components/common/EditProjectModal'
import ProjectMembersPanel from '../components/common/ProjectMembersPanel'

const VIEW_TYPES = {
  board: { icon: HiViewBoards, label: 'Board' },
  list: { icon: HiViewList, label: 'List' },
  table: { icon: HiTable, label: 'Table' },
  calendar: { icon: HiCalendar, label: 'Calendar' },
}

export default function ProjectPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { setCurrentProject } = useProjectContext()
  
  const [project, setProject] = useState(null)
  const [boards, setBoards] = useState([])
  const [selectedBoard, setSelectedBoard] = useState(null)
  const [viewType, setViewType] = useState(() => {
    // Persist view preference
    return localStorage.getItem('projectViewType') || 'board'
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [showCreateBoard, setShowCreateBoard] = useState(false)
  const [showEditProject, setShowEditProject] = useState(false)
  const [showMembers, setShowMembers] = useState(false)

  // Persist view type changes
  useEffect(() => {
    localStorage.setItem('projectViewType', viewType)
  }, [viewType])

  // Reset state when switching projects
  useEffect(() => {
    setProject(null)
    setBoards([])
    setSelectedBoard(null)
    setError(null)
  }, [projectId])

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [projectRes, boardsRes] = await Promise.all([
        projectsApi.get(projectId),
        projectsApi.getBoards(projectId),
      ])
      setProject(projectRes.data)
      // Ensure boards is always an array
      const boardsData = Array.isArray(boardsRes.data) ? boardsRes.data : []
      setBoards(boardsData)
      setCurrentProject(projectRes.data)
      
      // Always select the first board for the current project
      if (boardsData.length > 0) {
        setSelectedBoard(boardsData[0])
      } else {
        setSelectedBoard(null)
      }
    } catch (error) {
      console.error('Failed to fetch project:', error)
      if (error.response?.status === 404) {
        navigate('/')
      } else if (error.isAuthLoop) {
        setError('Authentication error. Please try logging out and back in, or contact support.')
      } else {
        setError('Failed to load project. Please try refreshing the page.')
      }
    } finally {
      setLoading(false)
    }
  }, [projectId, navigate, setCurrentProject])

  useEffect(() => {
    fetchProject()
    
    return () => {
      setCurrentProject(null)
    }
  }, [fetchProject, setCurrentProject])

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return
    }
    try {
      await projectsApi.delete(projectId)
      navigate('/')
    } catch (error) {
      console.error('Failed to delete project:', error)
    }
  }

  const handleCreateBoard = async (data) => {
    try {
      const response = await boardsApi.create(projectId, data)
      setBoards(prev => [...prev, response.data])
      setSelectedBoard(response.data)
      setShowCreateBoard(false)
    } catch (error) {
      console.error('Failed to create board:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => fetchProject()}
              className="btn btn-secondary"
            >
              Try Again
            </button>
            <a
              href="/.auth/logout?post_logout_redirect_uri=/"
              className="btn btn-primary"
            >
              Logout & Re-login
            </a>
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-16">
        <p className="text-surface-500 dark:text-surface-400">Project not found</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Project Header */}
      <div className="mb-5 flex items-start justify-between gap-6 border-b border-surface-200 pb-5 dark:border-surface-700 xl:pb-6">
        <div className="flex items-center gap-3">
          <span
            className="w-4 h-4 rounded-full shrink-0"
            style={{ backgroundColor: project.color || '#3b82f6' }}
          />
          <div>
            <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">{project.name}</h1>
            {project.description && (
              <p className="text-surface-500 dark:text-surface-400 mt-1">{project.description}</p>
            )}
            {project.notes && (
              <button
                onClick={() => setShowEditProject(true)}
                className="text-sm text-brand-600 hover:text-brand-700 mt-1 flex items-center gap-1"
              >
                <HiDocumentText className="w-4 h-4" />
                View notes
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMembers(true)}
            className="btn btn-secondary"
            title="Project Members"
          >
            <HiUserGroup className="w-4 h-4 mr-2" />
            Members
          </button>
          <button
            onClick={() => setShowEditProject(true)}
            className="btn btn-secondary"
          >
            <HiPencil className="w-4 h-4 mr-2" />
            Edit
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg"
            aria-label="Project actions"
            aria-expanded={showSettings}
          >
            <HiDotsVertical className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Board Tabs + View Toggle */}
      <div className="mb-4 flex items-center justify-between gap-6 border-b border-surface-200 pb-2 dark:border-surface-700">
        {/* Board Tabs */}
        <div className="flex items-center gap-2">
          {boards.map(board => (
            <button
              key={board.id}
              onClick={() => setSelectedBoard(board)}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                selectedBoard?.id === board.id
                  ? 'bg-white dark:bg-surface-800 border border-b-0 border-surface-200 dark:border-surface-700 text-brand-600 -mb-[1px]'
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700'
              }`}
            >
              {board.name}
            </button>
          ))}
          <button
            onClick={() => setShowCreateBoard(true)}
            className="p-2 text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg"
            title="Add board"
          >
            <HiPlus className="w-4 h-4" />
          </button>
        </div>

        {/* View Toggle */}
        <div className="flex items-center bg-surface-100 dark:bg-surface-700 rounded-lg p-1">
          {Object.entries(VIEW_TYPES).map(([key, { icon: Icon, label }]) => (
            <button
              key={key}
              onClick={() => setViewType(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewType === key
                  ? 'bg-white dark:bg-surface-800 text-brand-600 shadow-sm'
                  : 'text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200'
              }`}
              title={label}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Board Content */}
      <div className="flex-1 overflow-hidden">
        {selectedBoard ? (
          <>
            {viewType === 'board' && (
              <BoardView board={selectedBoard} onUpdate={fetchProject} />
            )}
            {viewType === 'list' && (
              <ListView board={selectedBoard} onUpdate={fetchProject} />
            )}
            {viewType === 'table' && (
              <TableView board={selectedBoard} onUpdate={fetchProject} />
            )}
            {viewType === 'calendar' && (
              <CalendarView board={selectedBoard} onUpdate={fetchProject} />
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-surface-500 dark:text-surface-400 mb-4">No boards yet</p>
            <button
              onClick={() => setShowCreateBoard(true)}
              className="btn btn-primary"
            >
              <HiPlus className="w-5 h-5 mr-2" />
              Create Board
            </button>
          </div>
        )}
      </div>

      {/* Settings Dropdown */}
      {showSettings && (
        <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)}>
          <div
            className="absolute right-6 top-24 w-48 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg shadow-lg py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowSettings(false)
                setShowEditProject(true)
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-700"
            >
              <HiCog className="w-4 h-4" />
              Project Settings
            </button>
            <button
              onClick={() => {
                setShowSettings(false)
                handleDeleteProject()
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <HiTrash className="w-4 h-4" />
              Delete Project
            </button>
          </div>
        </div>
      )}

      {/* Create Board Modal */}
      {showCreateBoard && (
        <CreateBoardModal
          onClose={() => setShowCreateBoard(false)}
          onCreate={handleCreateBoard}
        />
      )}

      {/* Edit Project Modal */}
      {showEditProject && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditProject(false)}
          onUpdate={(updated) => {
            setProject(updated)
            setCurrentProject(updated)
            setShowEditProject(false)
          }}
        />
      )}

      {/* Members Panel */}
      {showMembers && (
        <ProjectMembersPanel
          project={project}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  )
}
